import { API_BASE } from "../shared/config/config";
import {
  DOCUMENT_DOWNLOAD_TYPES,
  DOCUMENT_TYPE_DISPLAY_SEPARATOR,
  DOCUMENT_TYPES_API_LIST_KEY,
  DOCUMENT_VIEW_CATEGORY_OTHER,
  DOCUMENT_VIEW_CATEGORY_STATEMENTS,
  DOCUMENT_VIEW_CATEGORY_TAX,
} from "../shared/constants/StatementsDocuments.constants";

const getDocumentTypeLabel = (item) => {
  if (typeof item === "string") {
    return item.trim();
  }
  return (
    item?.label ??
    item?.Label ??
    item?.name ??
    item?.Name ??
    ""
  ).trim();
};

const getDocumentTypeCategoryLabel = (item) => {
  if (typeof item === "string") {
    return "";
  }
  return (item?.categoryLabel ?? item?.CategoryLabel ?? "").trim();
};

export const formatDocumentTypeDisplay = (categoryLabel, label) => {
  if (categoryLabel) {
    return `${categoryLabel}${DOCUMENT_TYPE_DISPLAY_SEPARATOR}${label}`;
  }
  return label;
};

/** Normalizes API title/documentType strings that use legacy " >> " separators. */
export const formatDocumentGridTitle = (value) => {
  if (!value?.length) {
    return value;
  }
  return value.replace(/\s*>>\s*/g, DOCUMENT_TYPE_DISPLAY_SEPARATOR);
};

const toDocumentTypeOption = (item) => {
  const label = getDocumentTypeLabel(item);
  if (!label) {
    return null;
  }
  const categoryLabel = getDocumentTypeCategoryLabel(item);
  const schemaId = (
    typeof item === "string" ? "" : item?.schemaId ?? item?.SchemaId ?? ""
  ).trim();
  return {
    label,
    categoryLabel,
    schemaId: schemaId || label,
    displayText: formatDocumentTypeDisplay(categoryLabel, label),
  };
};

export function parseActiveDocumentTypeOptions(data) {
  if (!data) {
    return [];
  }

  let list = data;
  if (!Array.isArray(data)) {
    list =
      data[DOCUMENT_TYPES_API_LIST_KEY] ??
      data.documentTypes ??
      data.types ??
      [];
  }

  return list
    .map(toDocumentTypeOption)
    .filter(Boolean)
    .sort((a, b) => a.displayText.localeCompare(b.displayText));
}

export function buildDocumentOptionsForAllCategories(data) {
  const options = parseActiveDocumentTypeOptions(data);
  return {
    [DOCUMENT_VIEW_CATEGORY_STATEMENTS]: options,
    [DOCUMENT_VIEW_CATEGORY_TAX]: options,
    [DOCUMENT_VIEW_CATEGORY_OTHER]: options,
  };
}

// Every document list on the configuration page is filled from the same definitions, and a
// re-render or a refreshed token is no reason to ask XDoc for them again, so callers share one
// request until the user being acted for changes.
let documentTypesRequest = { user: null, pending: null };

export function forgetDocumentTypes() {
  documentTypesRequest = { user: null, pending: null };
}

export async function getDocumentTypes(
  accessToken,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve) =>
      resolve({
        [DOCUMENT_TYPES_API_LIST_KEY]: DOCUMENT_DOWNLOAD_TYPES.map((label) => ({
          label,
          categoryLabel: "Servicing Documents",
          isActive: "1",
        })),
      }),
    );
  }

  const user = impersonatedUser ?? "";
  if (documentTypesRequest.pending && documentTypesRequest.user === user) {
    return documentTypesRequest.pending;
  }

  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;
  headers.append("Authorization", bearer);

  if (impersonatedUser) {
    headers.append("X-Impersonated-User", impersonatedUser);
  }

  const options = {
    method: "GET",
    headers,
  };

  const pending = fetch(
    `${API_BASE}/documents/document-types`,
    options,
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Document types request failed: ${response.status}`);
      }
      return response.json();
    })
    .catch((error) => {
      console.log(error);
      // A failed request is not worth keeping: the next caller should be able to try again.
      if (documentTypesRequest.pending === pending) {
        forgetDocumentTypes();
      }
      return null;
    });

  documentTypesRequest = { user, pending };
  return pending;
}

export async function getDocumentsByType(
  accessToken,
  loanId,
  documentType,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve) => resolve([]));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    headers.append("Authorization", bearer);

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "GET",
      headers: headers,
    };

    const queryParamString = `?documentType=${documentType}&loanId=${loanId}`;

    return fetch(`${API_BASE}/documents${queryParamString}`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

export async function downloadDocument(
  accessToken,
  loanId,
  documentId,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve) => resolve([]));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;
    headers.append("Authorization", bearer);

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "GET",
      headers: headers,
    };

    const queryParamString = `?documentId=${documentId}&loanId=${loanId}`;
    let filename = "";

    return fetch(`${API_BASE}/documents/download${queryParamString}`, options)
      .then((response) => {
        const disposition = response.headers.get("content-disposition");
        if (disposition && disposition.indexOf("attachment") !== -1) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(disposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }
        return response.blob();
      })
      .then((blob) => {
        if (window && document) {
          const href = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = href;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      })
      .catch((error) => console.log(error));
  }
}

/**
 * Fetches document bytes for in-app preview (e.g. full-screen modal).
 * @returns {{ blob: Blob, filename: string } | null} null when API integration is off in dev mock mode
 */
export async function fetchDocumentForView(
  accessToken,
  loanId,
  documentId,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return null;
  }

  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;
  headers.append("Authorization", bearer);

  if (impersonatedUser) {
    headers.append("X-Impersonated-User", impersonatedUser);
  }

  const queryParamString = `?documentId=${documentId}&loanId=${loanId}`;
  let filename = "";

  const response = await fetch(
    `${API_BASE}/documents/download${queryParamString}`,
    {
      method: "GET",
      headers,
    },
  );

  if (!response.ok) {
    throw new Error(`Document download failed: ${response.status}`);
  }

  const disposition = response.headers.get("content-disposition");
  if (disposition && disposition.indexOf("attachment") !== -1) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(disposition);
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, "");
    }
  }

  const blob = await response.blob();
  return { blob, filename };
}

export async function uploadDocument(
  accessToken,
  loanId,
  documentName,
  file,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, _) => resolve(""));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const body = new FormData();
    body.append("file", file, file.name);

    const options = {
      method: "POST",
      headers: headers,
      body: body,
    };

    const queryParamString = `?loanId=${loanId}&documentName=${documentName}`;

    return fetch(`${API_BASE}/documents/upload${queryParamString}`, options)
      .then((response) => response.json())
      .catch((error) => console.log(error));
  }
}

export async function requestPayoffQuote(
  accessToken,
  loanId,
  requestBody,
  impersonatedUser,
) {
  if (
    process.env.REACT_APP_API_INTEGRATION === "0" &&
    (!process.env.NODE_ENV || process.env.NODE_ENV === "development")
  ) {
    return new Promise((resolve, _) => resolve(""));
  } else {
    const headers = new Headers();
    const bearer = `Bearer ${accessToken}`;

    headers.append("Authorization", bearer);
    headers.append("Content-Type", "application/json");

    if (impersonatedUser) {
      headers.append("X-Impersonated-User", impersonatedUser);
    }

    const options = {
      method: "POST",
      headers: headers,
      body: JSON.stringify(requestBody),
    };

    return fetch(`${API_BASE}/loans/${loanId}/payOffRequest`, options)
      .then((response) => response)
      .catch((error) => console.log(error));
  }
}
